import { useEffect, useMemo, useState } from 'react';
import { thumbnailUrl } from '../app/base';
import { listDashboard, type BoardSummary, type Project } from '../sync/boardsApi';
import { viewCenter } from './actions';
import { createBoardCardAt, takeBoardPickerTarget } from './boardCard';
import { useBoard, usePaneStore } from './context';
import { PanelFrame } from './floating';
import { primeBoardLookup } from './items/boardSummary';

/**
 * Picks a board to embed as a card. Opened from the toolbar it creates a card at the view
 * centre; opened from a card's inspector ("Change board") it re-points that card instead.
 */
export function BoardPickerPanel({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const pane = usePaneStore();
  const [target] = useState(() => takeBoardPickerTarget());
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    listDashboard()
      .then((r) => {
        if (!alive) return;
        setBoards(r.boards);
        setProjects(r.projects);
      })
      .catch(() => {
        if (alive) setError('Could not load your boards');
      });
    return () => {
      alive = false;
    };
  }, []);

  const folderName = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects]);
  const shown = useMemo(() => {
    if (!boards) return [];
    const q = query.trim().toLowerCase();
    return boards
      .filter((b) => b.id !== board.boardId && !b.archived)
      .filter((b) => !q || b.title.toLowerCase().includes(q) || (b.projectId && folderName.get(b.projectId)?.toLowerCase().includes(q)))
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }, [boards, board.boardId, query, folderName]);

  const pick = (b: BoardSummary) => {
    if (target && board.index.has(target)) {
      primeBoardLookup(b);
      board.updateItem(target, { props: { boardId: b.id } });
      pane.getState().select([target]);
    } else {
      createBoardCardAt({ board, pane }, b, viewCenter(pane));
    }
    onClose();
  };

  return (
    <PanelFrame kind="boards" title={target ? 'Change board' : 'Board card'} className="board-picker" onClose={onClose}>
      <div className="insp-body">
        <div className="insp-muted">{target ? 'Point this card at another board.' : 'Embeds a board as a card. Double-click the card to open it.'}</div>
        <input
          className="insp-input"
          placeholder="Filter boards"
          value={query}
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && shown[0]) pick(shown[0]);
          }}
        />
        {error ? (
          <div className="bad">{error}</div>
        ) : boards === null ? (
          <div className="insp-muted">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="insp-muted">{query ? 'No board matches.' : 'No other boards yet.'}</div>
        ) : (
          <ul className="bp-list">
            {shown.map((b) => (
              <li key={b.id}>
                <button type="button" className="bp-row" onClick={() => pick(b)} title={b.title}>
                  <span className="bp-thumb">{b.thumbnailAt ? <img src={thumbnailUrl(b.id, b.thumbnailAt)} alt="" loading="lazy" decoding="async" /> : 'no preview'}</span>
                  <span className="bp-meta">
                    <span className="bp-title">{b.title}</span>
                    <span className="bp-sub">
                      {b.role !== 'owner' ? `${b.role}` : b.projectId ? (folderName.get(b.projectId) ?? 'folder') : 'top level'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PanelFrame>
  );
}
